import { AuthenticationResult, AuthenticationStatus } from '@mc-alberta/types';
import mapAuthenticationContext from '../methods/mapAuthenticationContext';
import { FidoAdapter } from '../adapters/fido';
import { LvlrAdapter } from '../adapters/lvlr';
import { ThreeDsAdapter } from '../adapters/threeds';
import { MdesAdapter } from '../adapters/mdes';
import { AuthenticationStrategy } from '../types/enums/AuthenticationStrategy';
import { Kpi } from '../loggers/Kpi';
export class State {
    constructor(payload) {
        /** Current authentication session status. */
        this.authenticationStatus = AuthenticationStatus.NOT_SUPPORTED;
        /** Current authentication session result. */
        this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
        /** Key-value pair of applicable method types to their respective adapter instances. */
        this.adapters = {};
        /** The index of the current strategy. */
        this.strategyIndex = 0;
        /** Ordered list of all strategies that are allowed to be used during this session. */
        this.strategies = [];
        /**
         * Returns the adapter belonging to the given strategy.
         */
        this.getAdapter = (strategy) => {
            return this.adapters[strategy];
        };
        /**
         * Sets the given value of the given parameter on all adapters.
         */
        this.setAdapterStates = (attribute, value) => {
            for (const key in this.adapters) {
                this.adapters[key][attribute] = value;
            }
        };
        this.adapters = {
            [AuthenticationStrategy.LVLR]: new LvlrAdapter(payload),
            [AuthenticationStrategy.THREE_DS]: new ThreeDsAdapter(payload),
            [AuthenticationStrategy.FIDO2]: new FidoAdapter(payload),
            [AuthenticationStrategy.MDES]: new MdesAdapter(payload)
        };
        this.authenticationContext = payload.authenticationContext;
        this.accountReference = payload.accountReference;
        this.srcClientId = payload.srcClientId;
        this.srcCorrelationId = payload.srcCorrelationId;
        this.srciTransactionId = payload.srciTransactionId;
        this.traceId = payload.traceId;
        this.serviceId = payload.serviceId;
        this.cardToken = payload.cardToken;
        this.suppressChallenge = payload.suppressChallenge;
        this.kpi = new Kpi(payload);
    }
    /** The active strategy. */
    get strategy() {
        return this.strategies[this.strategyIndex];
    }
    set strategy(strategy) {
        this.strategyIndex = this
            .strategies
            .findIndex(t => t === strategy);
    }
    /** The active authentication method. */
    get authenticationMethod() {
        var _a;
        return (_a = this.getAdapter(this.strategy)) === null || _a === void 0 ? void 0 : _a.authenticationMethod;
    }
    /** The authentication methods that are available for the active strategy. */
    get authenticationMethods() {
        var _a;
        return ((_a = this.getAdapter(this.strategy)) === null || _a === void 0 ? void 0 : _a.authenticationMethods) || [];
    }
    /** The reference to the cardholder in SRC. */
    get accountReference() {
        return this._accountReference;
    }
    set accountReference(accountReference) {
        this.setAdapterStates('accountReference', accountReference);
        this._accountReference = accountReference;
    }
    /** Contextual data for the authentication session. */
    set authenticationContext(authenticationContext) {
        this.setAdapterStates('authenticationContext', mapAuthenticationContext(authenticationContext));
        this._authenticationContext = authenticationContext;
    }
    get authenticationContext() {
        return this._authenticationContext;
    }
    /** UI-defined authentication method attributes. */
    set methodAttributes(methodAttributes) {
        this.setAdapterStates('methodAttributes', methodAttributes);
    }
    get methodAttributes() {
        return Object
            .values(this.adapters)
            .reduce((methodAttributes, adapter) => (Object.assign(Object.assign({}, methodAttributes), adapter.methodAttributes)), {});
    }
    /**
     * The current card token.
     *
     * This may be the card token provided to Stark by the UI,
     * or it may be the one returned from a claimset (whichever one is newest).
     */
    get cardToken() {
        var _a;
        return (_a = this.getAdapter(this.strategy)) === null || _a === void 0 ? void 0 : _a.cardToken;
    }
    set cardToken(cardToken) {
        this.setAdapterStates('cardToken', cardToken);
    }
}
//# sourceMappingURL=state.js.map